import { isTrustedRedirectUrl } from '@/views/Auth/pages/B2BOrgResetPassword/utils/is-trusted-redirect-url';
import routes from '@/views/Auth/routes';

// A destination is safe to use verbatim only when it is a plain app-relative path
// with a SINGLE leading slash. `//evil.com` is protocol-relative (the browser
// resolves it against the current protocol), so it is an off-site navigation and
// must go through the trust check like any absolute url.
const isSafeRelativePath = (value: string) =>
  value.startsWith('/') && !value.startsWith('//');

/**
 * Open-redirect guard for the post-login navigation. `next_url` is echoed back by
 * the v5 mutation from the `?next=` query param this form sends, so it is
 * attacker-influenceable end to end. Before handing it to `window.location.replace`
 * it goes through the same trust check the rest of the Auth views use: same-origin
 * or a Crehana host passes, anything else falls back to the post-login landing.
 *
 * Absolute destinations are returned as the RESOLVED `href` rather than the raw
 * string, so the browser lands exactly where the check validated — `replace`
 * resolves relative values against the document URL, not the origin.
 */
export const resolveSafeDestination = (nextUrl: string): string => {
  const fallback = routes.onboardingNextUrl();

  if (!nextUrl) {
    return fallback;
  }

  if (isSafeRelativePath(nextUrl)) {
    return nextUrl;
  }

  if (!isTrustedRedirectUrl(nextUrl, window.location.origin)) {
    return fallback;
  }

  return new URL(nextUrl, window.location.origin).href;
};
